import { type JSX, ReactNode } from 'react';

import { SafeImage, Text, TextProps } from '@/ui';
import { imageMetaConverter } from '@lib/server';
import { APIImageType } from '@types';
import { clsx } from 'clsx';

/**
 * Interface for ImageWithCaption component properties.
 */
export interface ImageProps {
  /** The image object containing the image source and metadata. */
  image: APIImageType;
  /** Optional description of the image. */
  description?: string;
  /** Optional aspect ratio to apply to the image container. */
  aspectRatio?: string;
  /** Optional title to display alongside the image. */
  title?: string;
  /** Optional additional class names to apply to the container. */
  className?: string;
  /** Options for configuring the component's appearance and behavior. */
  options?: {
    /** Class names for different parts of the component. */
    classNames?: {
      /** Class name for the image element. */
      image?: string;
      /** Class name for the image parent container. */
      imageParent?: string;
      /** Class name for the text section containing the title and description. */
      textSection?: string;
      /** Class name for the title element. */
      title?: string;
      /** Class name for the description element. */
      description?: string;
    };
    /** Configuration for the text elements (title and description). */
    textElements?: {
      /** Configuration for the title element. */
      title?: {
        /** The HTML element type for the title (e.g., 'p', 'h1'). */
        element?: TextProps['element'];
        /** The style to apply to the title element. */
        elementStyle?: TextProps['elementStyle'];
      };
      /** Configuration for the description element. */
      description?: {
        /** The HTML element type for the description (e.g., 'span', 'p'). */
        element?: TextProps['element'];
        /** The style to apply to the description element. */
        elementStyle?: TextProps['elementStyle'];
      };
    };
  };
  /** Optional children to render inside the component. */
  children?: ReactNode;
}

/**
 * A component that renders an image with an optional caption consisting of a title and description.
 */
export const ImageWithCaptionComponent = ({
  image,
  title,
  description,
  aspectRatio,
  className,
  options,
  children,
}: ImageProps): JSX.Element | null => {
  if (!image) {
    return null;
  }

  const parsedImage = imageMetaConverter(image, {}, { includeDimensions: true });

  if (!imageMetaConverter(image)?.src) return null;

  aspectRatio = aspectRatio || `${parsedImage?.width || 1}/${parsedImage?.height || 1}`;

  return (
    <div style={{ aspectRatio }} className={clsx('bg-muted mx-auto flex flex-col', className)}>
      <div style={{ aspectRatio }} className="relative">
        <div className={clsx('absolute inset-0', options?.classNames?.imageParent)}>
          <SafeImage src={image} fill className={clsx('object-cover', options?.classNames?.image)} />
        </div>
      </div>
      {children}
      {(title || description) && (
        <div className={clsx('text-foreground p-2', options?.classNames?.textSection)}>
          {title && (
            <Text
              element={options?.textElements?.title?.element || 'p'}
              elementStyle={options?.textElements?.title?.elementStyle || 'h6'}
              className={clsx(options?.classNames?.title)}
            >
              {title}
            </Text>
          )}
          {description && (
            <Text
              element={options?.textElements?.description?.element || 'span'}
              elementStyle={options?.textElements?.description?.elementStyle}
              className={clsx(options?.classNames?.description)}
            >
              {description}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
